import React, { useCallback, useState } from 'react';

import axios, { AxiosError } from 'axios';
import debounce from 'lodash/debounce';

import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';

import TextField from '../TextField';
import FilterLabel from './FilterLabel';
import { filterPropTypes } from '../../../../utility/prop_types/CriteriaPropTypes';
import { Filter, StringFieldSettings, StringProperties } from './Interfaces/Filter';

function StringFilter({ tree, field, onChange, onClear }: Filter): JSX.Element {
    const classes = useStyles();
    const [options, setOptions] = useState<string[]>([]);
    const placeholder =
        (field?.settings as StringFieldSettings)?.placeholder ?? 'Any';

    // Use callback is used to maintain the debouce between rerenders so that the functions don't get repeatably called
    const debouncedAutocomplete = useCallback(
        debounce((facetId: number, facetValue: string) => {
            axios
                .get<string[]>(
                    `/api/facets/${facetId}/autocomplete?value=${facetValue}`
                )
                .then(response => {
                    // TODO - If autocomplete exact and matches options then valid needs to be set to true
                    setOptions(response.data);
                })
                .catch((error: AxiosError) => {
                    if (axios.isCancel(error)) {
                        console.log('cancel');
                    } else {
                        console.error(error);
                    }
                });
        }, 500),
        []
    );

    // Need to pass handleChange (rather than use directly) as it refs onChange which is function prop from parent
    // Otherwise that function will get cached which will ref an older version of the component the function belongs to
    // This can cause an older state to be used
    const debouncedHandleChange = useCallback(
        debounce(
            (
                onHandleChange: (value: string, options: boolean) => void,
                value: string,
                isValid: boolean
            ) => {
                onHandleChange(value, isValid);
            },
            1000
        ),
        []
    );

    function handleChange(value: string, isValid: boolean): void {
        if (value === '' && onClear) {
            onClear(tree.id);
        } else {
            const newTree = { ...tree };
            newTree.properties = {
                ...newTree.properties,
                operator: 'ilike',
                value: value
                // isValid: isValid
            };
            onChange(newTree);
        }
    }

    function handleTextFieldChange(
        event: React.ChangeEvent<HTMLInputElement>
    ): void {
        handleChange(event.target.value, true);
    }

    function handleAutocompleteInputChange(event: object, value: string): void {
        setOptions([]);
        if (field.settings) {
            const stringFieldSettings = field.settings as StringFieldSettings;
            // If exact match is required for autocomplete then set is valid to false while typing
            // TODO - If matches existing options then can set to true
            debouncedHandleChange(
                handleChange,
                value,
                !stringFieldSettings.autocompleteExactMatch
            );
            if (stringFieldSettings.autocompleteFacetId) {
                debouncedAutocomplete(
                    stringFieldSettings.autocompleteFacetId,
                    value
                );
            } else if (stringFieldSettings.autocompleteOptions) {
                setOptions(
                    stringFieldSettings.autocompleteOptions.filter(option =>
                        option.includes(value)
                    )
                );
            }
        }
    }

    function handleAutocompleteChange(event: object, value: string): void {
        debouncedHandleChange.cancel();
        console.log('handleAutocompleteChange', value);
        handleChange(value, true);
    }

    // function handleConfirm (event) {
    //     if (event.code === 'enter' && event.target.value) {
    //         // setAutoCompleteValue(autoCompleteValue.concat(e.target.value));
    //     }
    // }

    function autocompleteRenderInput(
        params: AutocompleteRenderInputParams
    ): JSX.Element {
        return (
            <TextField
                inputRef={params.InputProps.ref}
                inputProps={params.inputProps}
                name="value"
                placeholder={placeholder}
                fullWidth
                // onKeyDown={handleConfirm}
            />
        );
    }

    // OnEnter - remove focus on menu which should auto close on check whether exact match
    //

    return (
        <div className={classes.root}>
            <FilterLabel field={field} />
            {(field?.settings as StringFieldSettings)?.autocomplete ? (
                <Autocomplete
                    freeSolo
                    // getOptionLabel={option => option}
                    // blurOnSelect
                    // autoComplete
                    // autoHighlight
                    // autoSelect
                    // disableCloseOnSelect
                    clearOnBlur
                    clearOnEscape
                    disableClearable
                    options={options}
                    // groupBy={option => fields[option].category}
                    value={(tree.properties as StringProperties).value ?? ''}
                    renderInput={autocompleteRenderInput}
                    onChange={handleAutocompleteChange}
                    onInputChange={handleAutocompleteInputChange}
                />
            ) : (
                <TextField
                    name="value"
                    placeholder={placeholder}
                    value={(tree.properties as StringProperties).value ?? ''}
                    onChange={handleTextFieldChange}
                />
            )}
        </div>
    );
}

// TODO module augmentation to allow Theme to be used here instead of any
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        '&> *:not(:last-child)': {
            marginRight: 10
        },
        '&> div': {
            flexGrow: 1
        }
    }
}));

StringFilter.propTypes = filterPropTypes;

export default StringFilter;
